import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import t from './tokens';

// Per-student yearbook upsell (Shoob v2 Figma): each student can add a yearbook
// (cover variant) and an optional "+$X personalization" upgrade (photo + name on
// the cover). Multi-child gets "add for all" / "personalize all" shortcuts and a
// sticky total. Submits a batch of { student_image_id, variant_id, personalize }
// to the cart (CartBuilder#add_yearbook + add_addon for personalization).
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const s = {
  page: { background: t.pageBg, minHeight: '100vh', padding: '2rem 1.25rem 8rem' },
  inner: { maxWidth: 720, margin: '0 auto' },
  h1: {
    fontSize: 'clamp(1.4rem,3vw,2rem)', fontWeight: 700, color: t.ink, margin: '0 0 0.35rem', textAlign: 'center',
  },
  sub: { color: t.muted, margin: '0 0 1.5rem', textAlign: 'center' },
  card: {
    background: t.surface,
    border: `1px solid ${t.border}`,
    borderRadius: 14,
    boxShadow: t.shadowSm,
    padding: '1.25rem',
    marginBottom: '1rem',
  },
  infoTitle: { fontWeight: 700, color: t.ink, margin: '0 0 0.5rem' },
  feat: {
    listStyle: 'none', padding: 0, margin: 0, color: t.body, fontSize: '0.9rem', display: 'grid', gap: '0.35rem',
  },
  bulk: {
    display: 'flex', gap: '0.6rem', marginBottom: '1rem', flexWrap: 'wrap',
  },
  bulkBtn: {
    flex: '1 1 auto',
    padding: '0.6rem',
    borderRadius: t.radius,
    border: `1px solid ${t.brandBorder}`,
    background: t.surface,
    color: t.brandHover,
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    border: `1px solid ${t.border}`,
    borderRadius: 12,
    padding: '0.85rem',
    marginBottom: '0.75rem',
  },
  who: { flex: '1 1 140px', minWidth: 0 },
  name: { fontWeight: 700, color: t.ink },
  grade: { color: t.muted, fontSize: '0.85rem' },
  toggle: (on, accent) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    padding: '0.45rem 0.7rem',
    borderRadius: 10,
    cursor: 'pointer',
    minWidth: 92,
    border: `2px solid ${on ? accent : t.borderStrong}`,
    background: on ? accent : t.surface,
    color: on ? '#fff' : t.body,
    fontWeight: 600,
    fontSize: '0.78rem',
  }),
  toggleOff: { opacity: 0.4, cursor: 'not-allowed' },
  cover: {
    flex: '0 0 auto', border: `1px solid ${t.borderStrong}`, borderRadius: 8, padding: '0.3rem 0.4rem', fontSize: '0.85rem',
  },
  rowPrice: {
    width: 70, textAlign: 'right', fontWeight: 700, fontSize: '1.1rem', marginLeft: 'auto',
  },
  bar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 25,
    background: t.surface,
    borderTop: `1px solid ${t.border}`,
    boxShadow: '0 -4px 16px rgba(0,0,0,.1)',
    padding: '0.85rem 1.25rem',
  },
  barInner: {
    maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '0.5rem',
  },
  totalRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: t.muted, fontSize: '0.9rem',
  },
  cta: {
    width: '100%',
    background: t.brand,
    color: '#fff',
    border: 'none',
    borderRadius: t.radius,
    padding: '0.85rem',
    fontWeight: 700,
    fontSize: '1rem',
    cursor: 'pointer',
  },
  skip: {
    width: '100%',
    background: t.surface,
    color: t.body,
    border: `1px solid ${t.borderStrong}`,
    borderRadius: t.radius,
    padding: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

const YearbookUpsell = ({
  students, variants, personalization, adding, onSubmit, onSkip,
}) => {
  const variantById = useMemo(() => Object.fromEntries(variants.map((v) => [v.id, v])), [variants]);
  const defaultVariantId = variants[0]?.id || null;
  const persPrice = personalization?.price || 0;
  const persAvailable = !!personalization && persPrice > 0;

  const [rows, setRows] = useState(() => Object.fromEntries(
    students.map((st) => [st.student_image_id, { yearbook: false, variantId: defaultVariantId, personalize: false }]),
  ));

  const update = (id, patch) => setRows((r) => ({ ...r, [id]: { ...r[id], ...patch } }));
  const toggleYearbook = (id) => update(id, {
    yearbook: !rows[id].yearbook, personalize: rows[id].yearbook ? false : rows[id].personalize,
  });
  const togglePersonalize = (id) => {
    if (rows[id].yearbook && persAvailable) update(id, { personalize: !rows[id].personalize });
  };

  const allOn = students.every((st) => rows[st.student_image_id].yearbook);
  const anyPers = students.some((st) => rows[st.student_image_id].personalize);
  const selectAll = () => setRows((r) => Object.fromEntries(
    students.map((st) => [st.student_image_id, allOn
      ? { ...r[st.student_image_id], yearbook: false, personalize: false }
      : { ...r[st.student_image_id], yearbook: true }]),
  ));
  const personalizeAll = () => setRows((r) => Object.fromEntries(
    students.map((st) => [st.student_image_id, {
      ...r[st.student_image_id],
      personalize: anyPers ? false : r[st.student_image_id].yearbook,
    }]),
  ));

  const rowPrice = (id) => {
    const row = rows[id];
    if (!row.yearbook) return 0;
    return (variantById[row.variantId]?.price || 0) + (row.personalize ? persPrice : 0);
  };
  const selectedCount = students.filter((st) => rows[st.student_image_id].yearbook).length;
  const persCount = students.filter((st) => rows[st.student_image_id].personalize).length;
  const total = students.reduce((sum, st) => sum + rowPrice(st.student_image_id), 0);

  const submit = () => onSubmit(students
    .filter((st) => rows[st.student_image_id].yearbook)
    .map((st) => ({
      student_image_id: st.student_image_id,
      variant_id: rows[st.student_image_id].variantId,
      personalize: rows[st.student_image_id].personalize,
    })));

  const multi = students.length > 1;

  return (
    <div style={s.page}>
      <div style={s.inner}>
        <h1 style={s.h1}>{multi ? 'Add Yearbooks' : `Add ${students[0]?.name || 'your'} yearbook`}</h1>
        <p style={s.sub}>Preserve this year&rsquo;s memories forever.</p>

        <div style={s.card}>
          <div style={s.infoTitle}>
            {`School Yearbook${defaultVariantId ? ` — ${money(variantById[defaultVariantId].price)}` : ''}`}
          </div>
          <ul style={s.feat}>
            <li>✓ Class photos</li>
            <li>✓ School events &amp; field trips</li>
            <li>✓ Autograph pages with friends</li>
            {persAvailable && <li>{`✨ Add your photo & name to the cover for ${money(persPrice)}`}</li>}
          </ul>
        </div>

        {multi && (
          <div style={s.bulk}>
            <button type="button" style={s.bulkBtn} onClick={selectAll}>
              {allOn ? 'Deselect all' : 'Add yearbook for all'}
            </button>
            {persAvailable && selectedCount > 0 && (
              <button type="button" style={s.bulkBtn} onClick={personalizeAll}>
                {anyPers ? 'Remove personalization' : 'Personalize all'}
              </button>
            )}
          </div>
        )}

        {students.map((st) => {
          const row = rows[st.student_image_id];
          return (
            <div key={st.student_image_id} style={s.row}>
              <div style={s.who}>
                <div style={s.name}>{st.name}</div>
                {st.grade && <div style={s.grade}>{st.grade}</div>}
              </div>

              <button
                type="button"
                style={s.toggle(row.yearbook, t.brand)}
                onClick={() => toggleYearbook(st.student_image_id)}
              >
                <span>{row.yearbook ? '✓ Yearbook' : 'Yearbook'}</span>
                <span>{money(variantById[row.variantId]?.price)}</span>
              </button>

              {variants.length > 1 && row.yearbook && (
                <select
                  style={s.cover}
                  value={row.variantId}
                  onChange={(e) => update(st.student_image_id, { variantId: Number(e.target.value) })}
                >
                  {variants.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              )}

              {persAvailable && (
                <button
                  type="button"
                  style={{ ...s.toggle(row.personalize, '#7c3aed'), ...(row.yearbook ? {} : s.toggleOff) }}
                  onClick={() => togglePersonalize(st.student_image_id)}
                  disabled={!row.yearbook}
                >
                  <span>{row.personalize ? '✨ Personalized' : 'Personalize'}</span>
                  <span>{`+${money(persPrice)}`}</span>
                </button>
              )}

              <div style={{ ...s.rowPrice, color: rowPrice(st.student_image_id) > 0 ? t.brand : t.muted }}>
                {money(rowPrice(st.student_image_id))}
              </div>
            </div>
          );
        })}
      </div>

      <div style={s.bar}>
        <div style={s.barInner}>
          {selectedCount === 0 ? (
            <button type="button" style={s.skip} disabled={adding} onClick={onSkip}>
              No thanks, continue to cart
            </button>
          ) : (
            <>
              <div style={s.totalRow}>
                <span>{`${selectedCount + persCount} item${selectedCount + persCount === 1 ? '' : 's'} selected`}</span>
                <strong style={{ color: t.ink, fontSize: '1.2rem' }}>{money(total)}</strong>
              </div>
              <button type="button" style={s.cta} disabled={adding} onClick={submit}>
                {adding ? 'Adding…' : 'Add to cart & continue'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

YearbookUpsell.propTypes = {
  students: PropTypes.arrayOf(PropTypes.shape({
    student_image_id: PropTypes.number,
    name: PropTypes.string,
    grade: PropTypes.string,
  })).isRequired,
  variants: PropTypes.arrayOf(PropTypes.object).isRequired,
  personalization: PropTypes.shape({ variant_id: PropTypes.number, price: PropTypes.number }),
  adding: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

YearbookUpsell.defaultProps = { personalization: null, adding: false };

export default YearbookUpsell;
